import { supabase } from '../lib/supabase';

/**
 * KPI Service - Functions for KPI Board metrics
 *
 * Dashboards:
 * - Matt: Invoiced amount trend + hours per job
 * - Model Builders: First-pass approval rate
 * - Data Prep: Discrepancy response time + throughput
 * - Manager: Comprehensive view of all team KPIs
 */

// Employee IDs for role mapping
const EMPLOYEE_IDS = {
  ADAM: 1,        // Manager
  MATT: 5,        // Manager + Operations
  ADAM2: 2,       // Model Builder (aboyte)
  STEF: 6,        // Model Builder
  ZACH: 7,        // Model Builder
  DUSTIN: 4,      // Data Prep (dellis)
  MELVIN: 15,     // Data Prep (mlumag)
  HEATH: 13,      // Data Prep (hboyte)
};

// Email to employee ID mapping
const EMAIL_TO_EMPLOYEE = {
  'adam@ab-civil.com': EMPLOYEE_IDS.ADAM,
  'mcole@ab-civil.com': EMPLOYEE_IDS.MATT,
  'aboyte@ab-civil.com': EMPLOYEE_IDS.ADAM2,
  'squibete@ab-civil.com': EMPLOYEE_IDS.STEF,
  'zbreth@ab-civil.com': EMPLOYEE_IDS.ZACH,
  'dellis@ab-civil.com': EMPLOYEE_IDS.DUSTIN,
  'mlumag@ab-civil.com': EMPLOYEE_IDS.MELVIN,
  'hboyte@ab-civil.com': EMPLOYEE_IDS.HEATH,
};

// Manager emails (can view all KPIs and send to Corrections)
const MANAGER_EMAILS = ['adam@ab-civil.com', 'mcole@ab-civil.com'];

// Settings editors (can edit correction reasons)
const SETTINGS_EDITORS = ['adam@ab-civil.com', 'mcole@ab-civil.com'];

// Model builder employee IDs
const MODEL_BUILDER_IDS = [EMPLOYEE_IDS.ADAM2, EMPLOYEE_IDS.STEF, EMPLOYEE_IDS.ZACH];

// Data prep employee IDs (team-wide KPIs)
const DATA_PREP_IDS = [EMPLOYEE_IDS.DUSTIN, EMPLOYEE_IDS.MELVIN, EMPLOYEE_IDS.HEATH];

/**
 * Get employee ID from email
 */
export function getEmployeeIdFromEmail(email) {
  return EMAIL_TO_EMPLOYEE[email?.toLowerCase()] || null;
}

/**
 * Check if user is a manager
 */
export function isManager(email) {
  return MANAGER_EMAILS.includes(email?.toLowerCase());
}

/**
 * Check if user can send to corrections (only managers)
 */
export function canSendToCorrections(email) {
  return isManager(email);
}

/**
 * Check if user can edit correction reasons settings (Adam & Matt only)
 */
export function canEditCorrectionReasons(email) {
  return SETTINGS_EDITORS.includes(email?.toLowerCase());
}

/**
 * Get dashboard type for user
 */
export function getDashboardType(email) {
  const lowerEmail = email?.toLowerCase();

  if (MANAGER_EMAILS.includes(lowerEmail)) {
    return 'manager';
  }

  const employeeId = EMAIL_TO_EMPLOYEE[lowerEmail];

  if (MODEL_BUILDER_IDS.includes(employeeId)) {
    return 'model_builder';
  }

  if (DATA_PREP_IDS.includes(employeeId)) {
    return 'data_prep';
  }

  // Default to model builder view for unknown users
  return 'model_builder';
}

/**
 * Get biweekly periods for date range selection
 * Returns array of { startDate, endDate, label } objects
 */
export function getBiweekPeriods(count = 12) {
  const periods = [];
  const today = new Date();

  // Start from the most recent Sunday
  const currentPeriodStart = new Date(today);
  currentPeriodStart.setDate(today.getDate() - today.getDay());
  currentPeriodStart.setHours(0, 0, 0, 0);

  // Go back to align with biweekly schedule
  // Adjust to nearest biweek boundary
  const daysSinceEpoch = Math.floor(currentPeriodStart.getTime() / (1000 * 60 * 60 * 24));
  const weeksSinceEpoch = Math.floor(daysSinceEpoch / 7);
  const biweekOffset = weeksSinceEpoch % 2;

  if (biweekOffset === 1) {
    currentPeriodStart.setDate(currentPeriodStart.getDate() - 7);
  }

  for (let i = 0; i < count; i++) {
    const startDate = new Date(currentPeriodStart);
    startDate.setDate(startDate.getDate() - (i * 14));

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 13);

    const startStr = startDate.toISOString().split('T')[0];
    const endStr = endDate.toISOString().split('T')[0];

    const label = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

    periods.push({ startDate: startStr, endDate: endStr, label });
  }

  return periods;
}

/**
 * Get periods for KPI board: "Last 30 Days" (rolling) + calendar months for history
 * Each period has biweekly sub-periods for drill-down
 * Returns array of { startDate, endDate, label, biweeks: [{ startDate, endDate, label }] }
 */
export function getMonthlyPeriods(count = 6) {
  const periods = [];
  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];
  const shortDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // First entry: Last 30 days (rolling)
  const thirtyAgo = new Date(today);
  thirtyAgo.setDate(today.getDate() - 29); // 30 days inclusive
  const midpoint = new Date(thirtyAgo);
  midpoint.setDate(midpoint.getDate() + 14); // 15 days in
  const midpointNext = new Date(midpoint);
  midpointNext.setDate(midpointNext.getDate() + 1);

  periods.push({
    startDate: fmt(thirtyAgo),
    endDate: fmt(today),
    label: 'Last 30 Days',
    biweeks: [
      {
        startDate: fmt(thirtyAgo),
        endDate: fmt(midpoint),
        label: `${shortDate(thirtyAgo)} - ${shortDate(midpoint)}`
      },
      {
        startDate: fmt(midpointNext),
        endDate: fmt(today),
        label: `${shortDate(midpointNext)} - ${shortDate(today)}`
      }
    ]
  });

  // Remaining entries: calendar months going back
  for (let i = 0; i < count - 1; i++) {
    const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const label = firstDay.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    // Biweek sub-periods: 1st-15th and 16th-end
    const midDay = new Date(year, month, 15);
    const secondHalfStart = new Date(year, month, 16);

    periods.push({
      startDate: fmt(firstDay),
      endDate: fmt(lastDay),
      label,
      biweeks: [
        {
          startDate: fmt(firstDay),
          endDate: fmt(midDay),
          label: `${shortDate(firstDay)} - ${shortDate(midDay)}`
        },
        {
          startDate: fmt(secondHalfStart),
          endDate: fmt(lastDay),
          label: `${shortDate(secondHalfStart)} - ${shortDate(lastDay)}`
        }
      ]
    });
  }

  return periods;
}

// ============================================
// Matt's KPIs
// ============================================

/**
 * Get Matt's KPIs: Invoiced amount trend + hours per job
 * Target: < 1.25 hours per job
 */
export async function getMattKPIs(startDate, endDate) {
  try {
    // Get projects invoiced in date range
    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select('id, job_number, rev, name, model_price, invoiced_date')
      .gte('invoiced_date', startDate)
      .lte('invoiced_date', endDate)
      .not('model_price', 'is', null)
      .gt('model_price', 0);

    if (projectsError) throw projectsError;

    // Get Matt's time entries for these projects
    const { data: timeEntries, error: timeError } = await supabase
      .from('manictime_tags')
      .select('project_id, duration_hours, tag_name')
      .eq('employee_id', EMPLOYEE_IDS.MATT)
      .gte('start_time', startDate)
      .lte('start_time', endDate + 'T23:59:59');

    if (timeError) throw timeError;

    // Calculate total invoiced amount
    const totalInvoiced = projects.reduce((sum, p) => sum + (parseFloat(p.model_price) || 0), 0);
    const projectCount = projects.length;

    // Calculate total hours on project work (numeric job tags only, exclude overhead)
    const OVERHEAD_TAGS = ['200', '201', '202', '203', '204', '205', '206', '207', '208', '400', '401'];
    const projectHours = timeEntries
      .filter(e => /^\d+$/.test(e.tag_name) && !OVERHEAD_TAGS.includes(e.tag_name))
      .reduce((sum, e) => sum + (parseFloat(e.duration_hours) || 0), 0);

    // Hours per job
    const hoursPerJob = projectCount > 0 ? projectHours / projectCount : 0;
    const isWithinTarget = hoursPerJob <= 1.25;

    return {
      totalInvoiced: Math.round(totalInvoiced * 100) / 100,
      projectCount,
      projectHours: Math.round(projectHours * 100) / 100,
      hoursPerJob: Math.round(hoursPerJob * 100) / 100,
      isWithinTarget,
      target: 1.25,
      projects: projects.map(p => ({
        id: p.id,
        jobNumber: p.job_number,
        rev: p.rev,
        name: p.name,
        modelPrice: parseFloat(p.model_price) || 0,
        invoicedDate: p.invoiced_date
      }))
    };
  } catch (error) {
    console.error('Error fetching Matt KPIs:', error);
    throw error;
  }
}

/**
 * Get Matt's invoiced trend over multiple periods (monthly)
 */
export async function getMattInvoicedTrend(periodCount = 6) {
  const periods = getMonthlyPeriods(periodCount);
  const trend = [];

  for (const period of periods) {
    const kpis = await getMattKPIs(period.startDate, period.endDate);
    trend.push({
      period: period.label,
      startDate: period.startDate,
      endDate: period.endDate,
      totalInvoiced: kpis.totalInvoiced,
      projectCount: kpis.projectCount,
      hoursPerJob: kpis.hoursPerJob,
      isWithinTarget: kpis.isWithinTarget
    });
  }

  return trend.reverse(); // Oldest first for chart display
}

// ============================================
// Model Builder KPIs
// ============================================

/**
 * Get model builder's first-pass approval rate
 * First-pass = project went from Review → Complete without going to Corrections first
 * Only counts first correction per project (not multiple round-trips)
 * Excludes erosion projects (rev = 999)
 */
export async function getModelBuilderKPIs(employeeId, startDate, endDate) {
  try {
    // Get projects completed in date range where this builder was assigned
    const { data: completedProjects, error: projectsError } = await supabase
      .from('project_status_changes')
      .select(`
        project_id,
        changed_at,
        projects!inner (
          id,
          job_number,
          rev,
          name,
          model_builder_id
        )
      `)
      .eq('status_field', 'modeling_status')
      .eq('new_value', 'Complete')
      .gte('changed_at', startDate)
      .lte('changed_at', endDate + 'T23:59:59')
      .eq('projects.model_builder_id', employeeId)
      .neq('projects.rev', 999); // Exclude erosion

    if (projectsError) throw projectsError;

    // Get unique project IDs
    const projectIds = [...new Set(completedProjects.map(p => p.project_id))];

    if (projectIds.length === 0) {
      return {
        totalCompleted: 0,
        firstPassCount: 0,
        correctionCount: 0,
        approvalRate: 0,
        projects: []
      };
    }

    // Check which projects had corrections (first correction only)
    const { data: corrections, error: correctionsError } = await supabase
      .from('kpi_corrections_log')
      .select('project_id')
      .in('project_id', projectIds)
      .eq('is_first_correction', true);

    if (correctionsError) throw correctionsError;

    const projectsWithCorrections = new Set(corrections.map(c => c.project_id));

    const totalCompleted = projectIds.length;
    const correctionCount = projectsWithCorrections.size;
    const firstPassCount = totalCompleted - correctionCount;
    const approvalRate = totalCompleted > 0 ? (firstPassCount / totalCompleted) * 100 : 0;

    // Build project details
    const projectDetails = completedProjects.map(p => ({
      projectId: p.project_id,
      jobNumber: p.projects.job_number,
      rev: p.projects.rev,
      name: p.projects.name,
      completedAt: p.changed_at,
      hadCorrections: projectsWithCorrections.has(p.project_id)
    }));

    return {
      totalCompleted,
      firstPassCount,
      correctionCount,
      approvalRate: Math.round(approvalRate * 10) / 10,
      projects: projectDetails
    };
  } catch (error) {
    console.error('Error fetching model builder KPIs:', error);
    throw error;
  }
}

/**
 * Get all model builders' KPIs for manager dashboard
 * Uses kpi_model_builder_tracking table directly to include in-progress failures
 */
export async function getAllModelBuilderKPIs(startDate, endDate) {
  const results = [];

  // Get employee names
  const { data: employees } = await supabase
    .from('employees')
    .select('id, name')
    .in('id', MODEL_BUILDER_IDS);

  const employeeNames = {};
  employees?.forEach(e => { employeeNames[e.id] = e.name; });

  for (const builderId of MODEL_BUILDER_IDS) {
    // Query kpi_model_builder_tracking directly
    // Include: completed in period OR has corrections (even if not completed yet)
    const { data: tracking, error } = await supabase
      .from('kpi_model_builder_tracking')
      .select('*')
      .eq('model_builder_id', builderId)
      .or(`completed_period.gte.${startDate},corrections_against_builder.gt.0`);

    if (error) {
      console.error('Error fetching model builder tracking:', error);
      continue;
    }

    // Filter to period - completed in period OR has corrections logged in period
    const inPeriod = tracking.filter(t => {
      const completedInPeriod = t.completed_period &&
        t.completed_period >= startDate &&
        t.completed_period <= endDate;
      const hasCorrections = t.corrections_against_builder > 0;
      return completedInPeriod || hasCorrections;
    });

    // Count completed (has completed_at) vs in-progress failures
    const completed = inPeriod.filter(t => t.completed_at);
    const totalCompleted = completed.length;
    const correctionCount = inPeriod.filter(t => t.corrections_against_builder > 0).length;
    const firstPassCount = completed.filter(t => t.status === 'passed').length;
    const approvalRate = totalCompleted > 0 ? (firstPassCount / totalCompleted) * 100 : 0;

    results.push({
      employeeId: builderId,
      employeeName: employeeNames[builderId] || `Employee ${builderId}`,
      totalCompleted,
      firstPassCount,
      correctionCount,
      approvalRate: Math.round(approvalRate * 10) / 10,
      // Include in-progress failures for visibility
      inProgressFailures: inPeriod.filter(t => !t.completed_at && t.corrections_against_builder > 0)
    });
  }

  return results;
}

// ============================================
// Data Prep KPIs
// ============================================

/**
 * Get data prep discrepancy response times
 * Target: ≤ 3 business days (net of holds) for discrepancy check subitem
 * Uses kpi_data_prep_tracking table
 */
export async function getDataPrepKPIs(startDate, endDate) {
  try {
    // Get completed records in date range (passed or failed status)
    const { data: tracking, error } = await supabase
      .from('kpi_data_prep_tracking')
      .select('*')
      .in('status', ['passed', 'failed'])
      .gte('subitem_completed_at', startDate)
      .lte('subitem_completed_at', endDate + 'T23:59:59');

    if (error) throw error;

    const totalCompleted = tracking.length;
    const withinTarget = tracking.filter(t => t.status === 'passed').length;
    const outsideTarget = tracking.filter(t => t.status === 'failed').length;

    // Calculate averages using net business days (excludes hold time)
    const avgBusinessDays = totalCompleted > 0
      ? tracking.reduce((sum, t) => sum + (t.net_business_days || 0), 0) / totalCompleted
      : 0;

    const targetRate = totalCompleted > 0 ? (withinTarget / totalCompleted) * 100 : 0;

    return {
      totalCompleted,
      withinTarget,
      outsideTarget,
      targetRate: Math.round(targetRate * 10) / 10,
      avgBusinessDays: Math.round(avgBusinessDays * 10) / 10,
      target: 3, // business days (net of holds)
      timers: tracking.map(t => ({
        projectId: t.project_id,
        jobNumber: t.job_number,
        projectName: t.project_name,
        businessDaysElapsed: t.net_business_days,
        grossDays: t.gross_business_days,
        holdDays: t.total_hold_business_days,
        isWithinTarget: t.status === 'passed',
        status: t.status,
        failReason: t.fail_reason,
        startupTimestamp: t.startup_at,
        completedAt: t.subitem_completed_at
      }))
    };
  } catch (error) {
    console.error('Error fetching data prep KPIs:', error);
    throw error;
  }
}

/**
 * Get data prep throughput (projects moved to Ready to Model)
 */
export async function getDataPrepThroughput(startDate, endDate) {
  try {
    const { data, error } = await supabase
      .from('project_status_changes')
      .select(`
        project_id,
        changed_at,
        changed_by_email,
        projects (
          id,
          job_number,
          rev,
          name
        )
      `)
      .eq('status_field', 'modeling_status')
      .eq('new_value', 'Ready to Model')
      .gte('changed_at', startDate)
      .lte('changed_at', endDate + 'T23:59:59');

    if (error) throw error;

    // Group by email (data prep person)
    const byPerson = {};
    for (const record of data) {
      const email = record.changed_by_email || 'unknown';
      if (!byPerson[email]) {
        byPerson[email] = {
          email,
          count: 0,
          projects: []
        };
      }
      byPerson[email].count++;
      byPerson[email].projects.push({
        projectId: record.project_id,
        jobNumber: record.projects?.job_number,
        name: record.projects?.name,
        changedAt: record.changed_at
      });
    }

    return {
      total: data.length,
      byPerson: Object.values(byPerson).sort((a, b) => b.count - a.count)
    };
  } catch (error) {
    console.error('Error fetching data prep throughput:', error);
    throw error;
  }
}

// ============================================
// Corrections Log
// ============================================

/**
 * Log a correction (when project is sent to Corrections status)
 * Also looks up the model builder's Slack ID for DM notifications
 *
 * @param {Object} params
 * @param {number} params.projectId
 * @param {number} params.modelBuilderId
 * @param {string} params.reason - Primary reason (legacy, for display)
 * @param {string[]} params.selectedReasons - Array of all selected reasons
 * @param {boolean} params.countsAgainstBuilder - Whether this counts against builder's KPI
 * @param {string} params.comment
 * @param {string} params.loggedByEmail
 * @param {string} params.loggedByUserId
 * @param {string} params.jobNumber
 * @param {string} params.projectName
 */
export async function logCorrection({
  projectId,
  modelBuilderId,
  reason,
  selectedReasons = [],
  countsAgainstBuilder = true,
  comment,
  loggedByEmail,
  loggedByUserId,
  jobNumber,
  projectName
}) {
  try {
    // Check if this is the first correction for this project
    const { data: existing } = await supabase
      .from('kpi_corrections_log')
      .select('id')
      .eq('project_id', projectId)
      .limit(1);

    const isFirstCorrection = !existing || existing.length === 0;

    // Look up the model builder's email for DM notifications
    let modelBuilderEmail = null;
    if (modelBuilderId) {
      const { data: employee } = await supabase
        .from('employees')
        .select('email')
        .eq('id', modelBuilderId)
        .single();
      modelBuilderEmail = employee?.email || null;
    }

    const { data, error } = await supabase
      .from('kpi_corrections_log')
      .insert({
        project_id: projectId,
        model_builder_id: modelBuilderId,
        model_builder_email: modelBuilderEmail,
        reason,
        selected_reasons: selectedReasons,
        counts_against_builder: countsAgainstBuilder,
        comment,
        logged_by_email: loggedByEmail,
        logged_by_user_id: loggedByUserId,
        is_first_correction: isFirstCorrection,
        job_number: jobNumber,
        project_name: projectName
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error logging correction:', error);
    throw error;
  }
}

/**
 * Get corrections log for a project
 */
export async function getProjectCorrections(projectId) {
  try {
    const { data, error } = await supabase
      .from('kpi_corrections_log')
      .select(`
        *,
        employees (id, name)
      `)
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data.map(c => ({
      ...c,
      modelBuilderName: c.employees?.name || 'Unknown'
    }));
  } catch (error) {
    console.error('Error fetching project corrections:', error);
    throw error;
  }
}

/**
 * Get all corrections in date range (for manager dashboard)
 */
export async function getCorrectionsInRange(startDate, endDate) {
  try {
    const { data, error } = await supabase
      .from('kpi_corrections_log')
      .select(`
        *,
        employees (id, name)
      `)
      .gte('created_at', startDate)
      .lte('created_at', endDate + 'T23:59:59')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data.map(c => ({
      ...c,
      modelBuilderName: c.employees?.name || 'Unknown'
    }));
  } catch (error) {
    console.error('Error fetching corrections in range:', error);
    throw error;
  }
}

/**
 * Update a correction log entry (manager only)
 */
export async function updateCorrection(correctionId, updates, userEmail) {
  if (!isManager(userEmail)) {
    throw new Error('Only managers can update corrections');
  }

  try {
    const updateData = {};

    if (updates.selectedReasons !== undefined) {
      updateData.selected_reasons = updates.selectedReasons;
      updateData.reason = updates.selectedReasons[0] || '';
    }
    if (updates.countsAgainstBuilder !== undefined) {
      updateData.counts_against_builder = updates.countsAgainstBuilder;
    }
    if (updates.comment !== undefined) {
      updateData.comment = updates.comment;
    }

    const { data, error } = await supabase
      .from('kpi_corrections_log')
      .update(updateData)
      .eq('id', correctionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating correction:', error);
    throw error;
  }
}

/**
 * Delete a correction log entry (manager only)
 */
export async function deleteCorrection(correctionId, userEmail) {
  if (!isManager(userEmail)) {
    throw new Error('Only managers can delete corrections');
  }

  try {
    const { error } = await supabase
      .from('kpi_corrections_log')
      .delete()
      .eq('id', correctionId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting correction:', error);
    throw error;
  }
}

// ============================================
// Model Builder Assignment
// ============================================

/**
 * Get list of model builders for assignment dropdown
 */
export async function getModelBuilders() {
  try {
    const { data, error } = await supabase
      .from('employees')
      .select('id, name')
      .in('id', MODEL_BUILDER_IDS)
      .order('name');

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching model builders:', error);
    throw error;
  }
}

/**
 * Assign model builder to project
 */
export async function assignModelBuilder(projectId, modelBuilderId) {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update({ model_builder_id: modelBuilderId })
      .eq('id', projectId)
      .select('id, model_builder_id')
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error assigning model builder:', error);
    throw error;
  }
}

// ============================================
// Correction Reasons (from database)
// ============================================

// Legacy fallback - used if DB fetch fails
export const CORRECTION_REASONS = [
  'Surface Boundaries (including/excluding)',
  'Documenting Critical Drainage Problems',
  'Water Draining towards Pads',
  'Water Draining towards drainage structures',
  'Curb modeled incorrectly',
  'Flat Spots',
  'Client adjustment/request',
  'Design Change'
];

/**
 * Get correction reasons from database (kpi_settings table)
 * Returns { counts_against: [...], does_not_count: [...] }
 */
export async function getCorrectionReasons() {
  try {
    const { data, error } = await supabase
      .from('kpi_settings')
      .select('setting_value, updated_at, updated_by_email')
      .eq('setting_key', 'correction_reasons')
      .single();

    if (error) throw error;

    return {
      counts_against: data?.setting_value?.counts_against || [],
      does_not_count: data?.setting_value?.does_not_count || [],
      updatedAt: data?.updated_at,
      updatedByEmail: data?.updated_by_email
    };
  } catch (error) {
    console.error('Error fetching correction reasons:', error);
    // Fallback to defaults
    return {
      counts_against: [
        'Surface Boundaries (including/excluding)',
        'Documenting Critical Drainage Problems',
        'Water Draining towards Pads',
        'Water Draining towards drainage structures',
        'Curb modeled incorrectly',
        'Flat Spots'
      ],
      does_not_count: [
        'Client adjustment/request',
        'Design Change'
      ],
      updatedAt: null,
      updatedByEmail: null
    };
  }
}

/**
 * Update correction reasons in database (managers only)
 */
export async function updateCorrectionReasons(reasons, userEmail) {
  try {
    const { error } = await supabase
      .from('kpi_settings')
      .update({
        setting_value: {
          counts_against: reasons.counts_against,
          does_not_count: reasons.does_not_count
        },
        updated_at: new Date().toISOString(),
        updated_by_email: userEmail
      })
      .eq('setting_key', 'correction_reasons');

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating correction reasons:', error);
    throw error;
  }
}

// ============================================
// Manager Action Items
// ============================================

/**
 * Get pending action items for manager dashboard
 * @param {string} actionType - Optional filter by type (e.g., 'stale_project')
 */
export async function getManagerActionItems(actionType = null) {
  try {
    let query = supabase
      .from('manager_action_items')
      .select(`
        *,
        projects (
          id,
          job_number,
          rev,
          name,
          slack_channel_id,
          model_builder_id
        ),
        roles (
          id,
          key,
          name
        ),
        employees (
          id,
          name,
          slack_userid
        )
      `)
      .eq('status', 'pending')
      .order('days_in_stage', { ascending: false });

    if (actionType) {
      query = query.eq('action_type', actionType);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data.map(item => ({
      ...item,
      projectDetails: item.projects,
      responsibleRole: item.roles,
      responsibleEmployee: item.employees
    }));
  } catch (error) {
    console.error('Error fetching manager action items:', error);
    throw error;
  }
}

/**
 * Dismiss an action item (mark as dismissed)
 */
export async function dismissActionItem(itemId, userEmail) {
  try {
    const { error } = await supabase
      .from('manager_action_items')
      .update({
        status: 'dismissed',
        dismissed_at: new Date().toISOString(),
        dismissed_by_email: userEmail
      })
      .eq('id', itemId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error dismissing action item:', error);
    throw error;
  }
}

/**
 * Mark action item as having had a Slack message sent
 */
export async function markActionItemMessageSent(itemId) {
  try {
    const { error } = await supabase
      .from('manager_action_items')
      .update({
        status: 'message_sent',
        slack_message_sent_at: new Date().toISOString()
      })
      .eq('id', itemId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error marking action item message sent:', error);
    throw error;
  }
}

/**
 * Queue a Slack message to be sent by Python automation
 */
export async function queueSlackMessage({
  channelId,
  message,
  projectId = null,
  actionItemId = null,
  triggeredByEmail,
  triggeredByEmployeeId = null
}) {
  try {
    const { data, error } = await supabase
      .from('slack_message_queue')
      .insert({
        channel_id: channelId,
        message,
        related_project_id: projectId,
        related_action_item_id: actionItemId,
        triggered_by_email: triggeredByEmail,
        triggered_by_employee_id: triggeredByEmployeeId
      })
      .select()
      .single();

    if (error) throw error;

    // Also mark the action item as message sent
    if (actionItemId) {
      await markActionItemMessageSent(actionItemId);
    }

    return data;
  } catch (error) {
    console.error('Error queuing Slack message:', error);
    throw error;
  }
}

/**
 * Get stage to role mapping for reference
 */
export async function getStageRoleMapping() {
  try {
    const { data, error } = await supabase
      .from('stage_role_mapping')
      .select(`
        *,
        roles (
          id,
          key,
          name
        )
      `);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching stage role mapping:', error);
    throw error;
  }
}

// ============================================
// Export all functions
// ============================================

export const KPIService = {
  // User helpers
  getEmployeeIdFromEmail,
  isManager,
  canSendToCorrections,
  canEditCorrectionReasons,
  getDashboardType,
  getBiweekPeriods,
  getMonthlyPeriods,

  // Matt's KPIs
  getMattKPIs,
  getMattInvoicedTrend,

  // Model Builder KPIs
  getModelBuilderKPIs,
  getAllModelBuilderKPIs,

  // Data Prep KPIs
  getDataPrepKPIs,
  getDataPrepThroughput,

  // Corrections
  logCorrection,
  getProjectCorrections,
  getCorrectionsInRange,
  getCorrectionReasons,
  updateCorrectionReasons,
  updateCorrection,
  deleteCorrection,
  CORRECTION_REASONS,

  // Model Builder Assignment
  getModelBuilders,
  assignModelBuilder,

  // Manager Action Items
  getManagerActionItems,
  dismissActionItem,
  markActionItemMessageSent,
  queueSlackMessage,
  getStageRoleMapping,

  // Constants
  EMPLOYEE_IDS,
  MODEL_BUILDER_IDS,
  DATA_PREP_IDS,
  MANAGER_EMAILS
};

export default KPIService;
